import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail, ArrowLeft, Shield } from 'lucide-react';
import { 
  baseTransitions, 
  entranceAnimations, 
  hoverAnimations, 
  formAnimations,
  buttonAnimations
} from '../../utils/animations';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (error) {
      setError('Credenciais inválidas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-blue-50 flex items-center justify-center p-4">
      {/* Main Container */}
      <div className="w-full max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          
          {/* Left Side - Branding */}
          <div className={`text-center lg:text-left ${entranceAnimations.slideInFromLeft}`}>
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-xl mb-6">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 font-display">
                PlayKids
              </h1>
              <p className="text-xl text-slate-600 mb-2">
                Sistema de Contratos
              </p>
              <p className="text-sm text-slate-500">
                Área Administrativa
              </p>
            </div>
            
            {/* Company Info */}
            <div className="hidden lg:block space-y-4 text-sm text-slate-600">
              <div className="flex items-center justify-center lg:justify-start space-x-4">
                <div className="text-right">
                  <div className="font-medium text-slate-700">CNPJ: 31.692.815/0001-02</div>
                  <div className="text-slate-600">Marcionilia Breder Satler</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-slate-700">Play Kids</div>
                  <div className="text-slate-600">Ativa desde 04/10/2018</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Side - Login Form */}
          <div className={`${entranceAnimations.slideInFromRight}`}>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-200/50 p-8 lg:p-10">
              {/* Form Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Acesso Administrativo
                </h2>
                <p className="text-slate-600">
                  Faça login para acessar o sistema de gerenciamento
                </p>
              </div>
              
              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border border-slate-300 rounded-xl bg-white/50 backdrop-blur-sm ${formAnimations.fieldFocus} ${baseTransitions.normal}`}
                      placeholder="seu email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full pl-10 pr-12 py-3 border border-slate-300 rounded-xl bg-white/50 backdrop-blur-sm ${formAnimations.fieldFocus} ${baseTransitions.normal}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 ${baseTransitions.fast}`}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className={`bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl ${entranceAnimations.fadeIn}`}>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg ${hoverAnimations.scale} ${buttonAnimations.press} ${baseTransitions.normal} focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Entrando...</span>
                    </div>
                  ) : (
                    'Entrar no Sistema'
                  )}
                </button>
              </form>
              
              {/* Security Note */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center space-x-2 text-xs text-slate-600">
                  <Shield className="w-4 h-4 text-teal-500" />
                  <span>Sistema seguro com autenticação Firebase</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Back to Home Link */}
        <div className={`text-center mt-8 ${entranceAnimations.fadeIn}`}>
          <a 
            href="/" 
            className={`inline-flex items-center space-x-2 text-slate-600 hover:text-teal-600 font-medium ${baseTransitions.normal} ${hoverAnimations.lift}`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para a página inicial</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

